// src/pages/WhistleBlownCommentPage/WhistleBlownCommentPage.jsx
import React, { useEffect, useState, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { SmallPlainInsetBtn, SolidColorButton } from "../../components/buttons";
import {
  LeftArrow,
  TrashCanIcon,
  SkitsOlympicLogoIcon,
} from "../../components/icons";
import {
  apiFetchSuspendedCommunications,
  apiCommunicationGoLive,
} from "../../api/backend/admin";
import {
  apiDeleteSkitComment,
  apiDeleteCommentThread,
  apiDeleteThreadMemberResponse,
} from "../../api/backend/comment";
import { BASE_REPORT_FLOW } from "../../constants/reportFlow"; // wherever you export it
import "./whistleBlownCommentPage.scss";

function WhistleBlownCommentPage() {
  const [communications, setCommunications] = useState([]);
  const [paging, setPaging] = useState({ limit: 10, offset: 0 });
  const navigate = useNavigate();

  // Build a map from optionId -> label, so lookups are O(1)
  const reasonLabelMap = useMemo(() => {
    const map = {};
    BASE_REPORT_FLOW.forEach((step) => {
      if (Array.isArray(step.options)) {
        step.options.forEach((opt) => {
          map[opt.id] = opt.label;
        });
      }
    });
    return map;
  }, []);

  // Fetch a page of suspended items
  const loadSuspended = async () => {
    try {
      const res = await apiFetchSuspendedCommunications(paging);
      setCommunications((prev) => [...prev, ...res.data.data.suspended]);
    } catch (err) {
      console.error("Failed to load suspended communications", err);
    }
  };

  useEffect(() => {
    loadSuspended();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [paging]);

  const handleDelete = async ({ id, communicationType }) => {
    try {
      switch (communicationType) {
        case "comment":
          await apiDeleteSkitComment({ commentId: id });
          break;
        case "thread":
          await apiDeleteCommentThread({ threadId: id });
          break;
        case "response":
          await apiDeleteThreadMemberResponse({ responseId: id });
          break;
        default:
          throw new Error("Unknown type");
      }
      setCommunications((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Delete failed:", err);
    }
  };

  const handleGoLive = async ({ id, communicationType }) => {
    try {
      await apiCommunicationGoLive({ id, communicationType });
      setCommunications((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error("Go live failed:", err);
    }
  };

  return (
    <div id="topWhistleBlownCommentPage" className="whistle-blown-comment-page">
      <SolidColorButton
        solidColorBtnImage={
          <LeftArrow
            height="15px"
            width="15px"
            fillColor="#964b00"
            strokeWidth="4"
            strokeColor="#964b00"
          />
        }
        className="gold-btn"
        solidColorBtnText="Back"
        solidColorBtnFontWeight="800"
        solidColorBtnFontFamily="helvetica"
        solidColorBtnTextFontSize=".75rem"
        solidColorBtnFontColor="#964b00"
        solidColorBtnWidth="100px"
        solidColorBtnBoxShadow="0 4px 3px 1px #000000bb, 0 6px 8px #000000bb, 0 -4px 4px #000000bb, 0 -6px 4px #000000bb, inset 0 0 3px 0 #000000bb"
        onClick={() => navigate(-1)}
      />
      <h2>Suspended Communications</h2>
      <div className="reports-list">
        {communications.map((item) => (
          <div key={item.id} className="report-item">
            <div className="report-meta">
              <span>
                <strong>ID: </strong> {item.id}
              </span>
              <span>
                <strong>Type: </strong> {item.communicationType}
              </span>
              <span>
                <strong>Whistle Blown Count:</strong> {item.whistleBlownCount}
              </span>
            </div>
            <div className="report-info">
              <strong>Message: </strong> {item.message}
              {item.reportReasons?.length > 0 && (
                <div>
                  <strong>Reasons:</strong>{" "}
                  {item.reportReasons
                    .flat()
                    .map((r) => reasonLabelMap[r.optionId] || r.optionId)
                    .join(", ")}
                </div>
              )}
            </div>
            <div className="action-buttons">
              <SmallPlainInsetBtn
               image={
                  <SkitsOlympicLogoIcon
                    width="20px"
                    height="20px"
                    fillColor="var(--color-bloodred)"
                    style = {{
                      marginRight:"5px"
                    }}
                  />
                }
                text="Go Live"
                onClick={() => handleGoLive(item)}
              />
              <SmallPlainInsetBtn
                image={<TrashCanIcon />}
                text="Delete"
                onClick={() => handleDelete(item)}
              />
            </div>
          </div>
        ))}
      </div>
      {communications.length >= paging.limit && (
        <div className="load-more-container">
          <SmallPlainInsetBtn
            text="Load More"
            onClick={() =>
              setPaging((p) => ({ ...p, offset: p.offset + p.limit }))
            }
          />
        </div>
      )}
    </div>
  );
}

export default WhistleBlownCommentPage;
